import React, { useEffect, useState } from "react";
import { apiUrl } from "../../../apiConfig";
import "./AdminCareers.css";

const CATEGORY_OPTIONS = ["Management", "Engineering", "Administration", "Finance", "Operations"];
const LOCATION_OPTIONS = ["Office", "Site", "Remote"];
const TYPE_OPTIONS = ["Full-time", "Part-time", "Contract"];

function makeId(title) {
  const base = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return `${base || "job"}-${Date.now().toString(36)}`;
}

const EMPTY_JOB = {
  id: null,
  title: "",
  category: CATEGORY_OPTIONS[0],
  location: LOCATION_OPTIONS[0],
  type: TYPE_OPTIONS[0],
  shortDesc: "",
  fullDesc: "",
  requirements: [""],
};

function JobFormModal({ initialJob, onCancel, onSave }) {
  const [form, setForm] = useState(initialJob || EMPTY_JOB);
  const isEditing = Boolean(initialJob);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateRequirement = (index, value) => {
    setForm((prev) => {
      const next = [...prev.requirements];
      next[index] = value;
      return { ...prev, requirements: next };
    });
  };

  const addRequirement = () => {
    setForm((prev) => ({ ...prev, requirements: [...prev.requirements, ""] }));
  };

  const removeRequirement = (index) => {
    setForm((prev) => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanedRequirements = form.requirements.map((r) => r.trim()).filter(Boolean);
    onSave({
      ...form,
      id: form.id || makeId(form.title),
      requirements: cleanedRequirements.length > 0 ? cleanedRequirements : [],
    });
  };

  return (
    <div className="admin-modal" role="dialog" aria-modal="true" aria-label={isEditing ? "Edit job" : "Add new job"}>
      <div className="admin-modal__backdrop" onClick={onCancel} />
      <div className="admin-modal__panel">
        <div className="admin-modal__header">
          <p className="admin-modal__title">{isEditing ? "Edit Job Posting" : "Add New Job"}</p>
          <button type="button" className="admin-modal__close" aria-label="Close" onClick={onCancel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form className="job-form" onSubmit={handleSubmit}>
          <div className="job-form__field">
            <label htmlFor="job-title">
              Job Title <span>*</span>
            </label>
            <input
              id="job-title"
              type="text"
              required
              placeholder="e.g. Site Engineer"
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
            />
          </div>

          <div className="job-form__row">
            <div className="job-form__field">
              <label htmlFor="job-category">
                Category <span>*</span>
              </label>
              <select
                id="job-category"
                required
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="job-form__field">
              <label htmlFor="job-location">
                Location <span>*</span>
              </label>
              <select
                id="job-location"
                required
                value={form.location}
                onChange={(e) => updateField("location", e.target.value)}
              >
                {LOCATION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="job-form__field">
            <label htmlFor="job-type">
              Employment Type <span>*</span>
            </label>
            <select
              id="job-type"
              required
              value={form.type}
              onChange={(e) => updateField("type", e.target.value)}
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="job-form__field">
            <label htmlFor="job-short-desc">
              Short Description <span>*</span>
            </label>
            <textarea
              id="job-short-desc"
              required
              placeholder="One or two lines shown on the card before &quot;Show more&quot;"
              value={form.shortDesc}
              onChange={(e) => updateField("shortDesc", e.target.value)}
            />
          </div>

          <div className="job-form__field">
            <label htmlFor="job-full-desc">
              Full Description <span>*</span>
            </label>
            <textarea
              id="job-full-desc"
              required
              placeholder="Full role description shown after &quot;Show more&quot;"
              value={form.fullDesc}
              onChange={(e) => updateField("fullDesc", e.target.value)}
            />
          </div>

          <div className="job-form__field">
            <label>Requirements</label>
            <div className="job-form__reqs">
              {form.requirements.map((req, index) => (
                <div className="job-form__req-row" key={index}>
                  <input
                    type="text"
                    placeholder={`Requirement ${index + 1}`}
                    value={req}
                    onChange={(e) => updateRequirement(index, e.target.value)}
                  />
                  <button
                    type="button"
                    className="job-form__req-remove"
                    aria-label="Remove requirement"
                    onClick={() => removeRequirement(index)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="job-form__add-req" onClick={addRequirement}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              Add requirement
            </button>
          </div>

          <div className="job-form__actions">
            <button type="button" className="job-form__cancel" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-accent job-form__submit">
              {isEditing ? "Save Changes" : "Publish Job"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ job, onCancel, onConfirm }) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className="admin-modal" role="dialog" aria-modal="true" aria-label="Confirm delete">
      <div className="admin-modal__backdrop" onClick={onCancel} />
      <div className="admin-modal__panel confirm-modal__panel">
        <span className="confirm-modal__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M10 11v6M14 11v6" />
          </svg>
        </span>
        <p className="confirm-modal__title">Delete this job posting?</p>
        <p className="confirm-modal__text">
          &ldquo;{job.title}&rdquo; will be removed from the Careers page
          immediately. This can&rsquo;t be undone.
        </p>
        <div className="confirm-modal__actions">
          <button type="button" className="job-form__cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="confirm-modal__delete" onClick={onConfirm}>
            Delete Job
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCareers() {
  const [isReady, setIsReady] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalMode, setModalMode] = useState(null); // null | "add" | "edit"
  const [editingJob, setEditingJob] = useState(null);
  const [deletingJob, setDeletingJob] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setIsReady(true), 30);
    return () => clearTimeout(t);
  }, []);

  const loadJobs = async () => {
    setIsLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("ihcc-admin-token") || sessionStorage.getItem("ihcc-admin-token");

      const headers = {
        "Content-Type": "application/json",
      };

      // Add token to Authorization header
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        // Also add as X-Admin-Token header for extra compatibility
        headers["X-Admin-Token"] = token;
      }

      const response = await fetch(apiUrl("/jobs.php"), {
        headers: headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to load job postings.");
      }

      // Check if data.jobs exists and is an array
      if (data.jobs && Array.isArray(data.jobs)) {
        setJobs(data.jobs);
      } else if (Array.isArray(data)) {
        setJobs(data);
      } else {
        console.warn("Unexpected jobs data format:", data);
        setJobs([]);
      }
    } catch (err) {
      setError(err.message || "Unable to load job postings.");
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const openAdd = () => {
    setEditingJob(null);
    setModalMode("add");
  };

  const openEdit = (job) => {
    setEditingJob(job);
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingJob(null);
  };

  const handleSave = async (job) => {
    setError("");

    try {
      const token = localStorage.getItem("ihcc-admin-token") || sessionStorage.getItem("ihcc-admin-token");
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        headers["X-Admin-Token"] = token;
      }

      const exists = jobs.some((entry) => entry.id === job.id);
      const url = exists ? `/jobs.php?id=${encodeURIComponent(job.id)}` : "/jobs.php";
      const response = await fetch(apiUrl(url), {
        method: exists ? "PUT" : "POST",
        headers: headers,
        body: JSON.stringify(job),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to save the job posting.");
      }

      setJobs((prev) => {
        const next = prev.some((entry) => entry.id === data.job.id)
          ? prev.map((entry) => (entry.id === data.job.id ? data.job : entry))
          : [data.job, ...prev];
        return next;
      });
      closeModal();
    } catch (err) {
      setError(err.message || "Unable to save the job posting.");
    }
  };

  const handleDelete = async () => {
    if (!deletingJob) return;

    setError("");

    try {
      const token = localStorage.getItem("ihcc-admin-token") || sessionStorage.getItem("ihcc-admin-token");
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        headers["X-Admin-Token"] = token;
      }

      const response = await fetch(apiUrl(`/jobs.php?id=${encodeURIComponent(deletingJob.id)}`), {
        method: "DELETE",
        headers: headers,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to delete the job posting.");
      }

      setJobs((prev) => prev.filter((job) => job.id !== deletingJob.id));
      setDeletingJob(null);
    } catch (err) {
      setError(err.message || "Unable to delete the job posting.");
    }
  };

  // Add safe check for jobs array before rendering
  const safeJobs = Array.isArray(jobs) ? jobs : [];

  return (
    <section className={`admin-careers ${isReady ? "admin-careers--ready" : ""}`}>
      <div className="container admin-careers__container">
        <div className="admin-careers__toolbar">
          <div>
            <p className="admin-careers__eyebrow">Careers Admin</p>
            <h1 className="admin-careers__title">Manage Job Postings</h1>
            <p className="admin-careers__subtitle">
              Add, edit, or remove the openings shown on the public Careers page.
            </p>
          </div>

          <div className="admin-careers__actions">
            <button type="button" className="btn btn-accent admin-careers__add" onClick={openAdd}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              Add New Job
            </button>
          </div>
        </div>

        {error && (
          <div className="admin-empty" role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="admin-empty">Loading job postings...</div>
        ) : safeJobs.length === 0 ? (
          <div className="admin-empty">
            No job postings yet. Click &ldquo;Add New Job&rdquo; to create one.
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {safeJobs.map((job) => (
                  <tr key={job.id || Math.random()}>
                    <td className="admin-table__title">{job.title || "Untitled"}</td>
                    <td>
                      <span className="admin-table__badge">{job.category || "N/A"}</span>
                    </td>
                    <td className="admin-table__muted">{job.location || "N/A"}</td>
                    <td className="admin-table__muted">{job.type || "N/A"}</td>
                    <td>
                      <div className="admin-table__actions">
                        <button
                          type="button"
                          className="admin-icon-btn"
                          aria-label={`Edit ${job.title || "job"}`}
                          onClick={() => openEdit(job)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="admin-icon-btn admin-icon-btn--danger"
                          aria-label={`Delete ${job.title || "job"}`}
                          onClick={() => setDeletingJob(job)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="admin-cards">
              {safeJobs.map((job) => (
                <div className="admin-card" key={job.id || Math.random()}>
                  <div className="admin-card__top">
                    <div>
                      <p className="admin-card__title">{job.title || "Untitled"}</p>
                      <span className="admin-table__badge">{job.category || "N/A"}</span>
                    </div>
                  </div>
                  <div className="admin-card__meta">
                    <span>{job.location || "N/A"}</span>
                    <span>&middot;</span>
                    <span>{job.type || "N/A"}</span>
                  </div>
                  <div className="admin-card__actions">
                    <button type="button" className="admin-icon-btn" onClick={() => openEdit(job)} aria-label={`Edit ${job.title || "job"}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="admin-icon-btn admin-icon-btn--danger"
                      onClick={() => setDeletingJob(job)}
                      aria-label={`Delete ${job.title || "job"}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {modalMode && (
        <JobFormModal initialJob={editingJob} onCancel={closeModal} onSave={handleSave} />
      )}

      {deletingJob && (
        <ConfirmDeleteModal
          job={deletingJob}
          onCancel={() => setDeletingJob(null)}
          onConfirm={handleDelete}
        />
      )}
    </section>
  );
}
